/**
 * "Missing CPP2 finder" — opened from the T4 prep note. Lists every imported stub
 * that was paid in the T4 year for one employee, the ones still missing a CPP2
 * amount first, with an inline box per stub so the whole year can be filled in
 * from one place. Each save hits the same PATCH the stub card uses.
 */
import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Check, Loader2 } from "lucide-react";
import api from "@/lib/api";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";

const money = (n) => (n == null ? "—" : `$${Number(n).toLocaleString("en-CA", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`);

function StubRow({ stub, onSaved }) {
  const [val, setVal] = useState(stub.cpp2 == null ? "" : String(stub.cpp2));
  const [saving, setSaving] = useState(false);
  const dirty = (val.trim() === "" ? null : Number(val)) !== (stub.cpp2 ?? null);
  const save = async () => {
    setSaving(true);
    try {
      const cpp2 = val.trim() === "" ? null : Number(val);
      const r = await api.patch(`/payroll/imported-paystubs/${stub.import_id}`, { cpp2 });
      onSaved(stub.import_id, r.data.cpp2);
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not save CPP2"); }
    finally { setSaving(false); }
  };
  const missing = stub.cpp2 == null;
  return (
    <tr className={`border-t border-gray-100 ${missing ? "bg-amber-50" : ""}`} data-testid={`cpp2-finder-row-${stub.import_id}`}>
      <td className="px-2 py-1.5 font-mono text-[11px] tabular-nums text-gray-800">{stub.pay_date}<p className="text-[9px] text-gray-500">{stub.period_start} → {stub.period_end}</p></td>
      <td className="px-2 py-1.5 text-right font-mono tabular-nums text-gray-800">{money(stub.cpp)}</td>
      <td className="px-2 py-1.5 text-right">
        <span className="inline-flex items-center gap-1">
          <input type="number" min="0" step="0.01" max={stub.cpp ?? undefined} value={val} placeholder="0.00"
            onChange={(e) => setVal(e.target.value)} onKeyDown={(e) => e.key === "Enter" && dirty && save()}
            data-testid={`cpp2-finder-input-${stub.import_id}`}
            className={`w-24 rounded border px-1.5 py-0.5 text-right font-mono text-xs tabular-nums text-gray-900 ${missing ? "border-amber-400 bg-white" : "border-gray-300 bg-white"}`} />
          <button type="button" onClick={save} disabled={saving || !dirty} data-testid={`cpp2-finder-save-${stub.import_id}`} aria-label="Save CPP2"
            className="rounded bg-[#1a3fd1] p-1 text-white disabled:opacity-30">{saving ? <Loader2 className="h-3 w-3 animate-spin" /> : <Check className="h-3 w-3" />}</button>
        </span>
      </td>
      <td className="px-2 py-1.5 text-right font-mono text-[9px] uppercase tracking-wider">
        {missing ? <span className="rounded bg-[#5a4208] px-1 py-0.5 font-bold text-[#fde68a]">missing</span> : <span className="text-emerald-700">base {money(Math.max(0, Number(stub.cpp) - Number(stub.cpp2)))}</span>}
      </td>
    </tr>
  );
}

export default function MissingCpp2Dialog({ employee, year, onClose }) {
  const [stubs, setStubs] = useState(null);
  const [err, setErr] = useState(null);

  useEffect(() => {
    let alive = true;
    api.get(`/payroll/imported-paystubs/${employee.employee_id}`)
      .then((r) => {
        if (!alive) return;
        const inYear = r.data.stubs.filter((s) => (s.pay_date || "").startsWith(String(year)) && !s.blank);
        // missing first, then oldest → newest so the year reads top to bottom
        inYear.sort((a, b) => (a.cpp2 == null ? 0 : 1) - (b.cpp2 == null ? 0 : 1) || (a.pay_date || "").localeCompare(b.pay_date || ""));
        setStubs(inYear);
      })
      .catch((e) => alive && setErr(e?.response?.data?.detail || "Could not load imported stubs"));
    return () => { alive = false; };
  }, [employee.employee_id, year]);

  const onSaved = (importId, cpp2) => {
    setStubs((xs) => xs.map((s) => (s.import_id === importId ? { ...s, cpp2 } : s)));
    toast.success(cpp2 == null ? "CPP2 cleared" : `CPP2 ${money(cpp2)} saved — box 16A updated`);
  };
  const missing = stubs ? stubs.filter((s) => s.cpp2 == null).length : 0;

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[85vh] overflow-y-auto bg-white text-gray-800 sm:max-w-xl" data-testid="cpp2-finder-dialog">
        <DialogHeader>
          <DialogTitle className="text-gray-900">CPP2 on imported stubs — {employee.first_name} {employee.last_name}, {year}</DialogTitle>
          <DialogDescription className="text-gray-600">
            Imported stubs print one combined CPP line. Type the CPP2 portion of each so the T4 fills box 16A instead of leaving it inside box 16.
            {stubs && (missing ? <strong className="ml-1 text-amber-700" data-testid="cpp2-finder-missing-count">{missing} still missing.</strong> : <strong className="ml-1 text-emerald-700" data-testid="cpp2-finder-missing-count">All entered.</strong>)}
          </DialogDescription>
        </DialogHeader>
        {err && <p className="text-xs text-red-600">{err}</p>}
        {!stubs && !err && <p className="flex items-center gap-1.5 font-mono text-[10px] text-gray-500"><Loader2 className="h-3 w-3 animate-spin" /> Loading imported stubs…</p>}
        {stubs && (stubs.length === 0 ? (
          <p className="text-xs text-gray-600" data-testid="cpp2-finder-empty">No imported stubs were paid in {year}.</p>
        ) : (
          <table className="w-full text-xs">
            <thead><tr className="font-mono text-[9px] uppercase tracking-widest text-gray-500">
              <th className="px-2 py-1 text-left font-normal">Paid · period</th>
              <th className="px-2 py-1 text-right font-normal">CPP line</th>
              <th className="px-2 py-1 text-right font-normal">CPP2 (16A)</th>
              <th className="px-2 py-1 text-right font-normal"></th>
            </tr></thead>
            <tbody>{stubs.map((s) => <StubRow key={s.import_id} stub={s} onSaved={onSaved} />)}</tbody>
          </table>
        ))}
      </DialogContent>
    </Dialog>
  );
}
